import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet, TextInput, View } from 'react-native'

import { BottomSheetTextInput } from '@gorhom/bottom-sheet'
import { Button, colors as zetColors } from '@zet/design-system'
import { Stack } from '@zet/design-system/layout'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import RNOtpVerify from 'react-native-otp-verify'

import ResetOtp from './resetOtp'
import VerifyOtpHeading from './verifyOtpHeading'

const OTP_COUNT = 4
const TIMER_COUNT = 30

type VerifyOtpProps = {
  mobileNumber?: string
  onReset: (phoneNumber: string) => void
  onClickSubmit: (otp: string, phoneNumber?: string) => void
  isVerifying?: boolean
  heading?: string
  withHeading: boolean
  withResend: boolean
  FooterComponent?: React.ElementType
  otpCount?: number
  timerCount?: number
}
const VerifyOtp = ({
  mobileNumber,
  onReset,
  onClickSubmit,
  isVerifying,
  heading,
  withResend = true,
  withHeading = true,
  FooterComponent,
  otpCount = OTP_COUNT,
  timerCount = TIMER_COUNT,
}: VerifyOtpProps) => {
  const [otp, setOtp] = useState<string[]>(Array(otpCount).fill(''))
  const { locale = 'en' } = zetApi.defaults.headers.common
  const inputRefs = useRef<(TextInput | null)[]>([])

  useEffect(() => {
    RNOtpVerify.getOtp()
      .then((p) => RNOtpVerify.addListener(otpHandler))
      .catch((p) => console.log(p))

    return () => RNOtpVerify.removeListener()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const otpHandler = (message: string) => {
    if (message) {
      const otpMatch: string | undefined = /(\d{4})/g.exec(message)?.[1]
      if (otpMatch) {
        const otpArray = otpMatch.split('')
        setOtp(otpArray)
        onClickSubmit(otpMatch, mobileNumber)
      }
    }
  }

  const handleOtpChange = (text: string, index: number) => {
    const numericText = text.replace(/[^0-9]/g, '')

    if (numericText.length > 1) {
      const digits = numericText.slice(0, otpCount).split('')
      const newOtp = [...otp]
      digits.forEach((digit, i) => {
        if (index + i < otpCount) {
          newOtp[index + i] = digit
        }
      })
      setOtp(newOtp)

      const nextIndex = Math.min(index + digits.length, otpCount - 1)
      inputRefs.current[nextIndex]?.focus()
    } else {
      const newOtp = [...otp]
      newOtp[index] = numericText
      setOtp(newOtp)

      if (numericText && index < otpCount - 1) {
        inputRefs.current[index + 1]?.focus()
      }
    }
  }

  const handleKeyPress = (
    e: { nativeEvent: { key: string } },
    index: number,
  ) => {
    if (e.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const onOTPSubmit = () => onClickSubmit(otp.join(''), mobileNumber)

  return (
    <Stack className="grow">
      {withHeading && heading && mobileNumber && (
        <VerifyOtpHeading heading={heading} mobileNumber={mobileNumber} />
      )}

      <Stack className="mx-8 mt-8 flex-row justify-around">
        {Array.from({ length: otpCount }).map((_, index) => (
          <BottomSheetTextInput
            key={index}
            // @ts-expect-error - BottomSheetTextInput ref type compatibility
            ref={(ref) => (inputRefs.current[index] = ref)}
            style={[styles.otpInput, otp[index] ? styles.otpInputFilled : {}]}
            value={otp[index]}
            onChangeText={(text) => handleOtpChange(text, index)}
            onKeyPress={(e) => handleKeyPress(e, index)}
            keyboardType="number-pad"
            maxLength={otpCount}
            selectTextOnFocus
            textAlign="center"
          />
        ))}
      </Stack>

      {withResend && (
        <Stack className="mt-4">
          <ResetOtp
            timerCount={timerCount}
            locale={String(locale)}
            handleSubmit={() => {
              onReset(mobileNumber || '')
              setOtp(Array(otpCount).fill(''))
              inputRefs.current[0]?.focus()
            }}
          />
        </Stack>
      )}
      {FooterComponent ? (
        <FooterComponent
          isValid={otp.join('').length === otpCount}
          onSubmit={onOTPSubmit}
        />
      ) : (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: zetColors.grays[40],
            marginTop: 60,
            width: '100%',
            padding: 12,
          }}
        >
          {/* @ts-expect-error - Button type compatibility */}
          <Button
            fullWidth
            onPress={onOTPSubmit}
            size={'lg'}
            loading={isVerifying}
          >
            {i18n.t('verifyPhoneNumber')}
          </Button>
        </View>
      )}
    </Stack>
  )
}

export default VerifyOtp

const styles = StyleSheet.create({
  otpInput: {
    width: 48,
    height: 48,
    fontSize: 16,
    fontWeight: '500',
    borderWidth: 1,
    borderColor: zetColors.grays[40],
    textAlign: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
    paddingHorizontal: 0,
  },
  otpInputFilled: {
    borderColor: zetColors.primary[50],
    backgroundColor: '#FFF',
  },
})
